// Alta de un código (react-hook-form + zod en espejo de type-kit) sobre el chasis común: secciones
// del editor y Crear/Descartar en la barra de abajo. Crear es la única vez que se edita.
import { useEffect, type ReactNode } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { LifecycleEditorShell, LifecycleNotice } from '@/shared/patterns/Lifecycle';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import {
    discountCodeFormSchema,
    type DiscountCodeFormValues,
    type DiscountCodeSaveError,
} from '../lib/discountCodeForm';
import { DiscountCodeFields } from './DiscountCodeFields';

export interface DiscountCodeFormProps {
    sources: Pick<PromotionFormSources, 'products' | 'productsState' | 'stores' | 'storesState'>;
    defaultValues: DiscountCodeFormValues;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    isSaving: boolean;
    /** Error del servidor que cuelga de un campo; uno nuevo por cada intento fallido. */
    saveError: DiscountCodeSaveError | null;
    onSubmit: (values: DiscountCodeFormValues) => void;
    onCancel: () => void;
    /** El campo de cadena del admin de tenant, arriba de todo. */
    retailerField?: ReactNode;
    /** Sin cadena elegida el resto no se llena: tiendas y equipos son de la cadena. */
    disabled?: boolean;
}

export function DiscountCodeForm({
    sources,
    defaultValues,
    today,
    isSaving,
    saveError,
    onSubmit,
    onCancel,
    retailerField,
    disabled = false,
}: DiscountCodeFormProps) {
    const form = useForm<DiscountCodeFormValues>({
        defaultValues,
        resolver: zodResolver(discountCodeFormSchema),
    });
    const { control, handleSubmit, setError } = form;
    const codeText = useWatch({ control, name: 'codeText' });
    const title = codeText.trim().toUpperCase();

    useEffect(() => {
        if (saveError?.field === 'codeText')
            setError(
                'codeText',
                { type: 'server', message: saveError.message },
                { shouldFocus: true },
            );
    }, [saveError, setError]);

    return (
        <LifecycleEditorShell
            ariaLabel="Nuevo código de descuento"
            title={
                <span className={title ? 'font-mono' : undefined}>{title || 'Código nuevo'}</span>
            }
            untitled={title === ''}
            dirty={false}
            subtitle="Se crea vigente; después ya no se edita, solo se anula."
            status={null}
            notices={
                <LifecycleNotice tone="info" role="note">
                    Lo usa cualquier cliente hasta agotar sus usos. Se captura antes de cotizar, uno
                    por venta, y baja el precio del equipo.
                </LifecycleNotice>
            }
            saveBar={{
                count: 1,
                message: 'Código nuevo sin guardar',
                saveLabel: 'Crear código',
                saving: isSaving,
                saveDisabled: disabled,
                onDiscard: onCancel,
                onSave: () => {
                    void handleSubmit(onSubmit)();
                },
            }}
        >
            {retailerField}
            <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
                <DiscountCodeFields form={form} sources={sources} today={today} origin="Admin VL" />
            </fieldset>
        </LifecycleEditorShell>
    );
}
